import { PropertyInfo, PropertyType } from './PropertyInfo';
import { SceneObject } from './SceneObject';
import { EmotionParticlesSceneObject } from './EmotionParticlesSceneObject';
import { ProgressBar } from './ProgressBar';
import { Vector2 } from './Vector2';
import { MethodInfo } from './MethodInfo';
import { GlowSceneObject } from './GlowSceneObject';
import { BitmapTextSceneObject } from './BitmapTextSceneObject';
import { TextSceneObject } from './TextSceneObject';
import { VerticalAlignment } from './VerticalAlignment';
import { DimensionSourceScale } from './DimensionSourceScale';
import { TextFormattingMode } from './TextFormattingMode';
import { Rect } from './Rect';
import { CheckBox } from './CheckBox';
import { ButtonState } from './ButtonState';
import { ButtonBase } from './ButtonBase';
import { Color4 } from './Color4';
import { ImageAdjust } from './ImageAdjust';
import { HorizontalAlignment } from './HorizontalAlignment';
import { DimensionSource } from './DimensionSource';
import { SoundSceneObject } from './SoundSceneObject';
import { EmotionParticleSystemParams } from './EmotionParticleSystemParams';
import { VideoSceneObject } from './VideoSceneObject';
import { RenderTargetSceneObject } from './RenderTargetSceneObject';
import { TextureSceneObject } from './TextureSceneObject';
import { TtfTextSceneObject } from './TtfTextSceneObject';
import { ScreenAlignmentObject } from './ScreenAlignmentObject';
import { TtfTextInputSceneObject } from './TtfTextInputSceneObject';
import { CgsEvent } from './Event';
import { SceneObjectType } from './SceneObjectType';

export class PropertyKeys {
  static readonly Id = 'Id';
  static readonly Position = 'Position';
  static readonly Origin = 'Origin';
  static readonly Scale = 'Scale';
  static readonly Skew = 'Skew';
  static readonly DrawOrder = 'DrawOrder';
  static readonly BlockEvents = 'BlockEvents';
  static readonly Touchable = 'Touchable';
  static readonly TouchArea = 'TouchArea';
  static readonly TimeScale = 'TimeScale';
  static readonly Bounds = 'Bounds';
  static readonly Size = 'Size';
  static readonly Hidden = 'Hidden';
  static readonly Down = 'Down';
  static readonly Color = 'Color';
  static readonly Saturation = 'Saturation';
  static readonly Contrast = 'Contrast';
  static readonly Brightness = 'Brightness';
  static readonly Hue = 'Hue';
  static readonly ColorOffset = 'ColorOffset';
  static readonly TextureName = 'TextureName';
  static readonly MaskSourceFrame = 'MaskSourceFrame';
  static readonly MaskScale = 'MaskScale';
  static readonly MaskPosition = 'MaskPosition';
  static readonly MaskOrigin = 'MaskOrigin';
  static readonly MaskSize = 'MaskSize';
  static readonly MaskSkew = 'MaskSkew';
  static readonly Frame = 'Frame';
  static readonly FontName = 'FontName';
  static readonly Text = 'Text';
  static readonly Format = 'Format';
  static readonly HAlignment = 'HAlignment';
  static readonly VAlignment = 'VAlignment';
  static readonly FontSize = 'FontSize';
  static readonly LineSpacing = 'LineSpacing';
  static readonly DimensionSource = 'DimensionSource';
  static readonly DimensionSourceScale = 'DimensionSourceScale';
  static readonly Spacing = 'Spacing';
  static readonly TextFormattingMode = 'TextFormattingMode';
  static readonly SoundName = 'SoundName';
  static readonly Volume = 'Volume';
  static readonly Loop = 'Loop';
  static readonly SpriteName = 'SpriteName';
  static readonly Offset = 'Offset';
  static readonly SourceDimension = 'SourceDimension';
  static readonly ActionId = 'ActionId';
  static readonly VideoName = 'VideoName';
  static readonly Width = 'Width';
  static readonly Height = 'Height';
  static readonly CoordinateSystem = 'CoordinateSystem';
  static readonly Active = 'Active';
  static readonly Visible = 'Visible';

  static contains(key: string): boolean {
    return Object.keys(PropertyKeys).some(
      (k) => k.toLowerCase() === key.toLowerCase().replace(/^_+/, '')
    );
  }

  static find(key: string): string | null {
    return (
      Object.keys(PropertyKeys).find(
        (k) => k.toLowerCase() === key.toLowerCase().replace(/^_+/, '')
      ) ?? null
    );
  }
}

export class SceneObjectMirror {
  private static _propertyMap: Record<string, PropertyInfo<any, any>>;
  public static _propertyMapByType: Record<string, Record<string, PropertyInfo<any, any>>>;
  private static _methodMap: Record<string, MethodInfo<any>>;

  static GetPropertyInfo(
    type: SceneObjectType,
    propertyName: string
  ): PropertyInfo<any, any> | null {
    if (!SceneObjectMirror._propertyMap) {
      SceneObjectMirror._InitPropertyMap();
      SceneObjectMirror._InitPropertyMapByType();
    }
    const properties = SceneObjectMirror._propertyMapByType[type];
    if (properties) {
      const property = properties[propertyName];
      if (property) {
        return property;
      }
    }
    return SceneObjectMirror._propertyMap[propertyName] ?? null;
  }

  static GetMethodInfo(methodName: string): MethodInfo<any> | null {
    if (!SceneObjectMirror._methodMap) {
      SceneObjectMirror._InitMethodMap();
    }
    return SceneObjectMirror._methodMap[methodName] ?? null;
  }

  private static _InitPropertyMap(): void {
    SceneObjectMirror._propertyMap = {};
    SceneObjectMirror._propertyMap[PropertyKeys.Id] = new PropertyInfo<SceneObject, string>(
      (o, v) => (o.id = v),
      '',
      PropertyType.String
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Position] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.position = v),
      Vector2.Zero,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Origin] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.pivot = v),
      Vector2.Zero,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Scale] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.scale = v),
      Vector2.One,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Skew] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.skew = v),
      Vector2.Zero,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.DrawOrder] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.z = v),
      0,
      PropertyType.Int
    );
    SceneObjectMirror._propertyMap[PropertyKeys.BlockEvents] = new PropertyInfo<
      SceneObject,
      boolean
    >((o, v) => (o.blockEvents = v), false, PropertyType.Bool);
    SceneObjectMirror._propertyMap[PropertyKeys.Touchable] = new PropertyInfo<SceneObject, boolean>(
      (o, v) => (o.touchable = v),
      false,
      PropertyType.Bool
    );
    SceneObjectMirror._propertyMap[PropertyKeys.TouchArea] = new PropertyInfo<SceneObject, Rect>(
      (o, v) => (o.touchArea = v),
      Rect.Empty,
      PropertyType.Rect
    );
    SceneObjectMirror._propertyMap[PropertyKeys.TimeScale] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.timeScale = v),
      1.0,
      PropertyType.Double
    );

    SceneObjectMirror._propertyMap[PropertyKeys.Bounds] = new PropertyInfo<SceneObject, Rect>(
      (o, v) => (o.bounds = v),
      Rect.Empty,
      PropertyType.Rect
    );

    SceneObjectMirror._propertyMap[PropertyKeys.Size] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.size = v),
      Vector2.Zero,
      PropertyType.Vector2,
      PropertyType.Size
    );

    SceneObjectMirror._propertyMap[PropertyKeys.Hidden] = new PropertyInfo<SceneObject, boolean>(
      (o, v) => {
        o.active = !v;
        o.visible = !v;
      },
      false,
      PropertyType.Bool
    );

    // Button
    SceneObjectMirror._propertyMap[PropertyKeys.Down] = new PropertyInfo<ButtonBase, boolean>(
      (o, v) => {
        if (o instanceof CheckBox) {
          o.checked = v;
        } else {
          o.state = v ? ButtonState.Down : ButtonState.Normal;
        }
      },
      false,
      PropertyType.Bool
    );

    SceneObjectMirror._propertyMap[PropertyKeys.Color] = new PropertyInfo<SceneObject, Color4>(
      (o, v) => (o.color = v),
      Color4.White,
      PropertyType.Color4,
      PropertyType.Color4i
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Saturation] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.imageAdjust.saturation = v),
      ImageAdjust.Default.saturation,
      PropertyType.Double
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Contrast] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.imageAdjust.contrast = v),
      ImageAdjust.Default.contrast,
      PropertyType.Double
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Brightness] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.imageAdjust.brightness = v),
      ImageAdjust.Default.brightness,
      PropertyType.Double
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Hue] = new PropertyInfo<SceneObject, number>(
      (o, v) => (o.imageAdjust.hue = v),
      ImageAdjust.Default.hue,
      PropertyType.Double
    );

    SceneObjectMirror._propertyMap[PropertyKeys.ColorOffset] = new PropertyInfo<
      SceneObject,
      Color4
    >(
      (o, v) => (o.imageAdjust.colorOffset = v),
      ImageAdjust.Default.colorOffset,
      PropertyType.Color4
    );

    // MaskSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.TextureName] = new PropertyInfo<
      SceneObject,
      string
    >(() => {}, '', PropertyType.String);
    SceneObjectMirror._propertyMap[PropertyKeys.MaskSourceFrame] = new PropertyInfo<
      SceneObject,
      Rect
    >((o, v) => (o.mask.sourceFrame = v), Rect.Empty, PropertyType.Rect);

    SceneObjectMirror._propertyMap[PropertyKeys.MaskScale] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.mask.scale = v),
      Vector2.One,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.MaskPosition] = new PropertyInfo<
      SceneObject,
      Vector2
    >((o, v) => (o.mask.position = v), Vector2.Zero, PropertyType.Vector2);
    SceneObjectMirror._propertyMap[PropertyKeys.MaskOrigin] = new PropertyInfo<
      SceneObject,
      Vector2
    >((o, v) => (o.mask.pivot = v), Vector2.Zero, PropertyType.Vector2);
    SceneObjectMirror._propertyMap[PropertyKeys.MaskSize] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.mask.size = v),
      Vector2.Zero,
      PropertyType.Vector2,
      PropertyType.Size
    );
    SceneObjectMirror._propertyMap[PropertyKeys.MaskSkew] = new PropertyInfo<SceneObject, Vector2>(
      (o, v) => (o.mask.skew = v),
      Vector2.Zero,
      PropertyType.Vector2
    );

    // SpriteSceneObject && VideoSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.Frame] = new PropertyInfo<any, number>(
      (o, v) => (o.frame = v),
      0,
      PropertyType.Int
    );

    // TextSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.FontName] = new PropertyInfo<
      TextSceneObject,
      string
    >(() => {}, '', PropertyType.String);
    SceneObjectMirror._propertyMap[PropertyKeys.Text] = new PropertyInfo<TextSceneObject, string>(
      (o, v) => (o.text = v),
      '',
      PropertyType.String
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Format] = new PropertyInfo<TextSceneObject, string>(
      (o, v) => (o.format = v),
      '',
      PropertyType.String
    );
    SceneObjectMirror._propertyMap[PropertyKeys.HAlignment] = new PropertyInfo<
      TextSceneObject,
      HorizontalAlignment
    >((o, v) => (o.halign = v), HorizontalAlignment.Left, PropertyType.HorizontalAlignment);
    SceneObjectMirror._propertyMap[PropertyKeys.VAlignment] = new PropertyInfo<
      TextSceneObject,
      VerticalAlignment
    >((o, v) => (o.valign = v), VerticalAlignment.Top, PropertyType.VerticalAlignment);
    SceneObjectMirror._propertyMap[PropertyKeys.FontSize] = new PropertyInfo<
      TextSceneObject,
      number
    >((o, v) => (o.fontSize = v), 14, PropertyType.Int);
    SceneObjectMirror._propertyMap[PropertyKeys.LineSpacing] = new PropertyInfo<
      TextSceneObject,
      number
    >((o, v) => (o.lineSpacing = v), 0, PropertyType.Int);

    // BitmapTextSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.DimensionSource] = new PropertyInfo<
      TextSceneObject,
      DimensionSource
    >(
      (o, v) => (o.textRenderParams.dimensionSource = v),
      DimensionSource.Frame,
      PropertyType.DimensionSource
    );
    SceneObjectMirror._propertyMap[PropertyKeys.DimensionSourceScale] = new PropertyInfo<
      TextSceneObject,
      DimensionSourceScale
    >(
      (o, v) => (o.textRenderParams.dimensionSourceScale = v),
      DimensionSourceScale.Both,
      PropertyType.DimensionSourceScale
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Spacing] = new PropertyInfo<
      BitmapTextSceneObject,
      number
    >((o, v) => (o.textRenderParams.letterSpacing = v), 0, PropertyType.Int);
    SceneObjectMirror._propertyMap[PropertyKeys.TextFormattingMode] = new PropertyInfo<
      TextSceneObject,
      TextFormattingMode
    >(
      (o, v) => (o.textFormattingMode = v),
      TextFormattingMode.Default,
      PropertyType.TextFormattingMode
    );

    // SoundSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.SoundName] = new PropertyInfo<SceneObject, string>(
      () => {},
      '',
      PropertyType.String
    );

    // SoundObject
    SceneObjectMirror._propertyMap[PropertyKeys.Volume] = new PropertyInfo<
      SoundSceneObject,
      number
    >((o, v) => (o.volume = v), 1.0, PropertyType.Double);
    SceneObjectMirror._propertyMap[PropertyKeys.Loop] = new PropertyInfo<any, boolean>(
      (o, v) => (o.loop = v),
      false,
      PropertyType.Bool
    );

    SceneObjectMirror._propertyMap[PropertyKeys.SpriteName] = new PropertyInfo<SceneObject, string>(
      () => {},
      '',
      PropertyType.String
    );

    // TileSceneObject && TextureSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.Offset] = new PropertyInfo<any, Vector2>(
      (o, v) => (o.offset = v),
      Vector2.Zero,
      PropertyType.Vector2
    );
    SceneObjectMirror._propertyMap[PropertyKeys.SourceDimension] = new PropertyInfo<
      TextureSceneObject,
      Vector2
    >((o, v) => (o.dimensionSource = v), Vector2.Zero, PropertyType.Vector2, PropertyType.Size);

    // ProgressBar
    SceneObjectMirror._propertyMap[PropertyKeys.ActionId] = new PropertyInfo<ProgressBar, string>(
      (o, v) => (o.actionId = v),
      '',
      PropertyType.String
    );

    // VideoSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.VideoName] = new PropertyInfo<
      VideoSceneObject,
      string
    >(() => {}, '', PropertyType.String);

    // RenderTargetSceneObject
    SceneObjectMirror._propertyMap[PropertyKeys.Width] = new PropertyInfo<
      RenderTargetSceneObject,
      number
    >((o, v) => (o.width = v), 0, PropertyType.Int);
    SceneObjectMirror._propertyMap[PropertyKeys.Height] = new PropertyInfo<
      RenderTargetSceneObject,
      number
    >((o, v) => (o.height = v), 0, PropertyType.Int);
    SceneObjectMirror._propertyMap[PropertyKeys.CoordinateSystem] = new PropertyInfo<
      RenderTargetSceneObject,
      Rect
    >((o, v) => (o.coordinateSystem = v), Rect.Empty, PropertyType.Rect);

    SceneObjectMirror._propertyMap[PropertyKeys.Active] = new PropertyInfo<SceneObject, boolean>(
      (o, v) => (o.active = v),
      false,
      PropertyType.Bool
    );
    SceneObjectMirror._propertyMap[PropertyKeys.Visible] = new PropertyInfo<SceneObject, boolean>(
      (o, v) => (o.visible = v),
      false,
      PropertyType.Bool
    );
  }

  private static _InitPropertyMapByType(): void {
    SceneObjectMirror._propertyMapByType = {};
    const emotion: Record<string, PropertyInfo<any, any>> = {};

    emotion['ParticlesFlags'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      () => {},
      0,
      PropertyType.Int
    );

    emotion['Duration'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.duration = v),
      EmotionParticleSystemParams.Default.duration,
      PropertyType.Double
    );

    emotion['MaxCount'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.maxCount = v),
      EmotionParticleSystemParams.Default.maxCount,
      PropertyType.Int
    );

    emotion['EmitterPosition'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.emitterPosition = v),
      EmotionParticleSystemParams.Default.emitterPosition,
      PropertyType.Vector2
    );

    emotion['PosVariance'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.positionVariance = v),
      EmotionParticleSystemParams.Default.positionVariance,
      PropertyType.Vector2
    );

    emotion['EmitAngle'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.angle = v),
      EmotionParticleSystemParams.Default.angle,
      PropertyType.Double
    );
    emotion['AngleVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.angleVariance = v),
      EmotionParticleSystemParams.Default.angleVariance,
      PropertyType.Double
    );

    emotion['LifeTime'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.lifeTime = v),
      EmotionParticleSystemParams.Default.lifeTime,
      PropertyType.Double
    );
    emotion['LifeTimeVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.lifeTimeVariance = v),
      EmotionParticleSystemParams.Default.lifeTimeVariance,
      PropertyType.Double
    );

    emotion['EmissionRate'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.emissionRate = v),
      EmotionParticleSystemParams.Default.emissionRate,
      PropertyType.Double
    );

    emotion['Gravity'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.gravity = v),
      EmotionParticleSystemParams.Default.gravity,
      PropertyType.Vector2
    );

    emotion['StartRotate'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.startRotate = v),
      EmotionParticleSystemParams.Default.startRotate,
      PropertyType.Double
    );
    emotion['StartRotateVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.startRotateVariance = v),
      EmotionParticleSystemParams.Default.startRotateVariance,
      PropertyType.Double
    );

    emotion['EndRotate'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.endRotate = v),
      EmotionParticleSystemParams.Default.endRotate,
      PropertyType.Double
    );
    emotion['EndRotateVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.endRotateVariance = v),
      EmotionParticleSystemParams.Default.endRotateVariance,
      PropertyType.Double
    );

    emotion['Speed'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.speed = v),
      EmotionParticleSystemParams.Default.speed,
      PropertyType.Double
    );
    emotion['SpeedVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.speedVariance = v),
      EmotionParticleSystemParams.Default.speedVariance,
      PropertyType.Double
    );

    emotion['TangentialAccel'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.tangentialAccel = v),
      EmotionParticleSystemParams.Default.tangentialAccel,
      PropertyType.Double
    );
    emotion['TangentialAccelVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.tangentialAccelVariance = v),
      EmotionParticleSystemParams.Default.tangentialAccelVariance,
      PropertyType.Double
    );

    emotion['RadialAccel'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.radialAccel = v),
      EmotionParticleSystemParams.Default.radialAccel,
      PropertyType.Double
    );
    emotion['RadialAccelVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.radialAccelVariance = v),
      EmotionParticleSystemParams.Default.radialAccelVariance,
      PropertyType.Double
    );

    emotion['StartSize'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.startSize = v),
      EmotionParticleSystemParams.Default.startSize,
      PropertyType.Vector2
    );
    emotion['StartSizeVariance'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.startSizeVariance = v),
      EmotionParticleSystemParams.Default.startSizeVariance,
      PropertyType.Vector2
    );

    emotion['EndSize'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.endSize = v),
      EmotionParticleSystemParams.Default.endSize,
      PropertyType.Vector2
    );
    emotion['EndSizeVariance'] = new PropertyInfo<EmotionParticlesSceneObject, Vector2>(
      (o, v) => (o.particleSystemParams.endSizeVariance = v),
      EmotionParticleSystemParams.Default.endSizeVariance,
      PropertyType.Vector2
    );

    emotion['StartColor'] = new PropertyInfo<EmotionParticlesSceneObject, Color4>(
      (o, v) => (o.particleSystemParams.startColor = v),
      EmotionParticleSystemParams.Default.startColor,
      PropertyType.Color4
    );
    emotion['StartColorVariance'] = new PropertyInfo<EmotionParticlesSceneObject, Color4>(
      (o, v) => (o.particleSystemParams.startColorVariance = v),
      EmotionParticleSystemParams.Default.startColorVariance,
      PropertyType.Color4
    );

    emotion['EndColor'] = new PropertyInfo<EmotionParticlesSceneObject, Color4>(
      (o, v) => (o.particleSystemParams.endColor = v),
      EmotionParticleSystemParams.Default.endColor,
      PropertyType.Color4
    );
    emotion['EndColorVariance'] = new PropertyInfo<EmotionParticlesSceneObject, Color4>(
      (o, v) => (o.particleSystemParams.endColorVariance = v),
      EmotionParticleSystemParams.Default.endColorVariance,
      PropertyType.Color4
    );

    emotion['StartFrame'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.startFrame = v),
      EmotionParticleSystemParams.Default.startFrame,
      PropertyType.Double
    );
    emotion['StartFrameVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.startFrameVariance = v),
      EmotionParticleSystemParams.Default.startFrameVariance,
      PropertyType.Double
    );

    emotion['FrameSpeed'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.frameSpeed = v),
      EmotionParticleSystemParams.Default.frameSpeed,
      PropertyType.Double
    );
    emotion['FrameSpeedVariance'] = new PropertyInfo<EmotionParticlesSceneObject, number>(
      (o, v) => (o.particleSystemParams.frameSpeedVariance = v),
      EmotionParticleSystemParams.Default.frameSpeedVariance,
      PropertyType.Double
    );

    SceneObjectMirror._propertyMapByType[SceneObjectType.Particles2] = emotion;

    const glow: Record<string, PropertyInfo<GlowSceneObject, any>> = {};
    glow['DimensionSource'] = new PropertyInfo<GlowSceneObject, DimensionSource>(
      () => {},
      DimensionSource.Frame,
      PropertyType.DimensionSource
    );
    glow['CoordinateSystem'] = new PropertyInfo<GlowSceneObject, Rect>(
      () => {},
      Rect.Empty,
      PropertyType.Rect
    );
    glow['GlowStrength'] = new PropertyInfo<GlowSceneObject, number>(
      () => {},
      0.0,
      PropertyType.Double
    );
    glow['GlowColor'] = new PropertyInfo<GlowSceneObject, Color4>(
      () => {},
      Color4.White,
      PropertyType.Color4
    );
    glow['BlurWidth'] = new PropertyInfo<GlowSceneObject, number>(
      () => {},
      0.0,
      PropertyType.Double
    );
    glow['BlurHeight'] = new PropertyInfo<GlowSceneObject, number>(
      () => {},
      0.0,
      PropertyType.Double
    );
    SceneObjectMirror._propertyMapByType[SceneObjectType.Glow] = glow;

    const alignment: Record<string, PropertyInfo<ScreenAlignmentObject, any>> = {};
    alignment['HAlignment'] = new PropertyInfo<ScreenAlignmentObject, HorizontalAlignment>(
      (o, v) => (o.halign = v),
      HorizontalAlignment.None,
      PropertyType.HorizontalAlignment
    );
    alignment['VAlignment'] = new PropertyInfo<ScreenAlignmentObject, VerticalAlignment>(
      (o, v) => (o.valign = v),
      VerticalAlignment.None,
      PropertyType.VerticalAlignment
    );
    SceneObjectMirror._propertyMapByType[SceneObjectType.ScreenAlignment] = alignment;

    const ttfLineSpacing: Record<string, PropertyInfo<TtfTextSceneObject, any>> = {};
    ttfLineSpacing['Spacing'] = new PropertyInfo<TtfTextSceneObject, number>(
      (o, v) => (o.textRenderParams.letterSpacing = v),
      0,
      PropertyType.Int
    );
    SceneObjectMirror._propertyMapByType[SceneObjectType.Ttf] = ttfLineSpacing;

    const ttfInputLineSpacing: Record<string, PropertyInfo<TtfTextInputSceneObject, any>> = {};
    ttfInputLineSpacing['Spacing'] = new PropertyInfo<TtfTextInputSceneObject, number>(
      (o, v) => (o.textRenderParams.letterSpacing = v),
      0,
      PropertyType.Int
    );
    SceneObjectMirror._propertyMapByType[SceneObjectType.TtfTextEdit] = ttfInputLineSpacing;
    SceneObjectMirror._propertyMapByType[SceneObjectType.TextEdit] = ttfInputLineSpacing;
  }

  private static _InitMethodMap(): void {
    SceneObjectMirror._methodMap = {};
    // SoundObject
    SceneObjectMirror._methodMap['Play'] = new MethodInfo<SoundSceneObject>((o) => o.play());
    SceneObjectMirror._methodMap['Stop'] = new MethodInfo<SoundSceneObject>((o) => o.stop());
    SceneObjectMirror._methodMap['Reset'] = new MethodInfo<any>((o) => o.reset());
    SceneObjectMirror._methodMap['SendSMEventAsync'] = new MethodInfo<SceneObject>(
      (o, p1) => new Promise(() => o.stateMachine?.dispatchEvent(p1 as CgsEvent))
    );
    SceneObjectMirror._methodMap['SendEventAsync'] = new MethodInfo<SceneObject>(
      (o, p1) => new Promise(() => o.sendEvent(p1 as CgsEvent))
    );
  }
}
